import type { QueryClient } from '@tanstack/react-query'
import {
  isLedgerSyncSyncStalled,
  LEDGERSYNC_SYNC_STALL_MS,
  readLedgerSyncPipeline,
  type LedgerSyncSyncPipeline,
} from '@dcp/shared/ledgersync/sync-state'
import type { ConnectionSyncState } from '@/lib/ledgersync-sync-activity'

/**
 * The Sync CLICKS made from this tab, and what to tell the user when each one
 * is over (DCP-401).
 *
 * PRODUCT RULE: ONE toast per click, and none for syncs nobody here started.
 * - A per-connection Sync (`single`) moves one or two pipelines of one bank.
 *   It is reported once, when every pipeline it started has finished: success
 *   → "<bank> synced"; any failure → one "<bank>: <raw reason(s)>" error.
 * - A fan-out ("Sync all transactions" / "Sync all balances") already said
 *   "Syncing N banks…" when it started. Per-bank successes would be N toasts
 *   of noise, so only per-bank FAILURES are reported, each as it happens.
 * - Scheduled / other-tab syncs are never in here, so they refresh silently.
 *
 * A click therefore owns a SET of members (connection × pipeline), each of
 * which settles as `completed`, `failed`, or `dead` (went idle without a
 * terminal status — a crashed/cancelled run past the stall ceiling).
 *
 * This module decides; it does not toast. {@link settleSyncClicks} returns the
 * notices to show and the watcher shows them — which keeps the rules testable
 * without a DOM.
 *
 * KEPT IN THE QUERY CACHE ({@link syncClicksKey}), per organization, so it is
 * scoped to the QueryClient (each test's fresh client gets a fresh registry)
 * and cleared with it on sign-out. CLAIM-ONCE: a member settles on the first report of its
 * finish and a click is removed once settled, so two mounted watchers cannot
 * toast the same click twice.
 */

/** What a click was. */
export type SyncClickKind = 'single' | 'fan-out'

/** One pipeline of one connection that a click started. */
export interface SyncClickMember {
  connectionId: string
  pipeline: LedgerSyncSyncPipeline
}

/** A pipeline of a connection that just went idle, as the watcher saw it. */
export interface SyncFinish extends SyncClickMember {
  /** The pipeline's status NOW: terminal, or a stale non-terminal one. */
  status: string | null
  /** The matching `*_error`. */
  error: string | null
  /** The bank's display name, for the notice. */
  displayName: string | null
}

/** A toast the watcher should show. */
export interface SyncClickNotice {
  kind: 'success' | 'error' | 'warning'
  message: string
}

/** How a member ended. */
type MemberOutcome = 'completed' | 'failed' | 'dead'

/** A member plus its outcome once settled. */
interface ClickMemberState extends SyncClickMember {
  outcome: MemberOutcome | null
  error: string | null
  displayName: string | null
}

/** One recorded click. */
interface SyncClick {
  kind: SyncClickKind
  /** Epoch ms of the accepted start. */
  startedAt: number
  members: ClickMemberState[]
}

/**
 * How long a click stays claimable. A run passes through at most three
 * non-terminal phases (`queued` → `refreshing` → `syncing`), and readers treat
 * any one lasting longer than {@link LEDGERSYNC_SYNC_STALL_MS} as dead — so a
 * clicked sync cannot legitimately finish later than three ceilings after the
 * click. Past that the click belongs to a run this tab never saw end (the user
 * was on another screen), and letting it claim the NEXT finish — tomorrow's
 * scheduled one — would report something the user did not start.
 */
const SYNC_CLICK_TTL_MS = 3 * LEDGERSYNC_SYNC_STALL_MS

/** Fallback for a `failed` sync that recorded no reason. */
const SYNC_FAILED_FALLBACK = 'Sync failed'

/**
 * Longest reason a toast carries. A vendor error can run to paragraphs; the
 * toast only has to say what went wrong, and the full text stays one hover
 * away in the row's failure indicator (`SyncFailureIndicator`).
 */
const TOAST_REASON_MAX = 160

/**
 * A failure reason cut to toast size, on a word boundary where there is one.
 *
 * @param reason - The raw `*_error`
 */
function toastReason(reason: string): string {
  if (reason.length <= TOAST_REASON_MAX) return reason
  const cut = reason.slice(0, TOAST_REASON_MAX)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > TOAST_REASON_MAX / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * Where the clicks live: the React Query cache, the app's home for "the user
 * just pressed Sync" state (Sage's `SAGE_KEYS.syncTriggerTime` is the
 * precedent). Clicks are client-only, never fetched; being in the cache scopes
 * them to the QueryClient and clears them with it on sign-out. Keyed by
 * ORGANIZATION like every tenant key: switching org only invalidates queries,
 * it does not clear the cache, so an org-less key would carry the previous
 * org's pending click (and its grace poll) into the next one.
 */
export const syncClicksKey = (organizationId: string | undefined) =>
  ['ledgersync', organizationId, 'sync-clicks'] as const

/**
 * The live clicks, oldest first, expired ones dropped — as a COPY, so settling
 * can work on it freely without mutating what the cache holds.
 */
function readClicks(
  queryClient: QueryClient,
  organizationId: string | undefined,
  now: number,
): SyncClick[] {
  return (queryClient.getQueryData<SyncClick[]>(syncClicksKey(organizationId)) ?? [])
    .filter((click) => now - click.startedAt <= SYNC_CLICK_TTL_MS)
    .map((click) => ({ ...click, members: click.members.map((member) => ({ ...member })) }))
}

/**
 * Store the click list. `gcTime: Infinity` because nothing ever OBSERVES this
 * key: an unobserved entry is garbage-collected after the default five minutes,
 * which would silently drop every click whose sync takes longer than that.
 * Expiry is {@link SYNC_CLICK_TTL_MS}'s job, applied on every read.
 */
function writeClicks(
  queryClient: QueryClient,
  organizationId: string | undefined,
  clicks: SyncClick[],
): void {
  queryClient.setQueryDefaults(syncClicksKey(organizationId), { gcTime: Infinity })
  queryClient.setQueryData<SyncClick[]>(syncClicksKey(organizationId), clicks)
}

/** Human name of a pipeline, for notices. */
const pipelineLabel = (pipeline: LedgerSyncSyncPipeline) =>
  pipeline === 'transactions' ? 'transaction' : 'balance'

/** Map a finish's current status to a member outcome. */
function outcomeOf(status: string | null): MemberOutcome {
  if (status === 'completed') return 'completed'
  if (status === 'failed') return 'failed'
  return 'dead'
}

/**
 * Record a click whose sync(s) the edge ACCEPTED. Call it from the mutation's
 * `onSuccess`, not on press — a refused start never runs, so it never ends.
 * A click with no members (nothing accepted) is not recorded.
 *
 * @param queryClient - The app's query client (the registry's scope)
 * @param organizationId - The organization the click was made in
 * @param kind - `single` for a per-connection Sync, `fan-out` for a "Sync all"
 * @param members - Every connection × pipeline the click started
 * @param now - Epoch ms of the start; defaults to the current time
 */
export function rememberSyncClick(
  queryClient: QueryClient,
  organizationId: string | undefined,
  kind: SyncClickKind,
  members: readonly SyncClickMember[],
  now: number = Date.now(),
): void {
  if (members.length === 0) return
  writeClicks(queryClient, organizationId, [
    ...readClicks(queryClient, organizationId, now),
    {
      kind,
      startedAt: now,
      members: members.map((member) => ({
        ...member,
        outcome: null,
        error: null,
        displayName: null,
      })),
    },
  ])
}

/**
 * Is there an UNSETTLED click younger than `withinMs`? Read-only.
 *
 * Drives the connection list's grace poll: the edge may enqueue a new run
 * without writing `queued` (see `BANK_CONNECTIONS_START_GRACE_MS`), and then a
 * recent click is the only evidence that something is about to move.
 *
 * @param queryClient - The app's query client (the registry's scope)
 * @param organizationId - The organization whose clicks count
 * @param withinMs - How recent a click still counts
 * @param now - Epoch ms; defaults to the current time
 */
export function hasPendingSyncClick(
  queryClient: QueryClient,
  organizationId: string | undefined,
  withinMs: number,
  now: number = Date.now(),
): boolean {
  return readClicks(queryClient, organizationId, now).some((click) => now - click.startedAt < withinMs)
}

/**
 * Settle the members these finishes belong to, and return the notices due.
 *
 * A finish settles the matching unsettled member of EVERY live click that
 * holds it (two clicks waiting on one deduplicated run are both answered).
 *
 * DON'T BLOCK ON A DEAD HALF. A `single` click with one member settled and
 * another still open looks at that other pipeline on the row: if it is idle
 * with a NON-TERMINAL status (stale — its run died) or the row is gone, it is
 * settled as `dead` right away instead of waiting up to the stall ceiling for
 * its own report. An idle pipeline with a TERMINAL status is left open: that
 * is the previous run's result, and this click's run has not reported yet.
 *
 * Notices, per settled click:
 * - `single`: any failure → one "<bank>: <reasons>" error, reasons
 *   de-duplicated;
 *   all completed → "<bank> synced"; some completed, rest dead → a warning
 *   naming the half that did not finish (no success is claimed for it); all
 *   dead → nothing (nothing is known about the outcome).
 * - `fan-out`: a "<bank>: <reason>" error per failed bank, emitted when that bank fails; nothing
 *   when it succeeds or dies.
 *
 * @param queryClient - The app's query client (the registry's scope)
 * @param organizationId - The organization the finished connections belong to
 * @param finishes - Pipelines that went idle in the latest snapshot
 * @param rowFor - The current row of a connection, for the dead-half check
 * @param now - Epoch ms; defaults to the current time
 * @returns The toasts to show, in order
 */
export function settleSyncClicks(
  queryClient: QueryClient,
  organizationId: string | undefined,
  finishes: readonly SyncFinish[],
  rowFor: (connectionId: string) => ConnectionSyncState | undefined,
  now: number = Date.now(),
): SyncClickNotice[] {
  const clicks = readClicks(queryClient, organizationId, now)
  const notices: SyncClickNotice[] = []
  const touched = new Set<SyncClick>()

  for (const finish of finishes) {
    const outcome = outcomeOf(finish.status)
    for (const click of clicks) {
      const member = click.members.find(
        (m) =>
          m.outcome === null &&
          m.connectionId === finish.connectionId &&
          m.pipeline === finish.pipeline,
      )
      if (!member) continue
      member.outcome = outcome
      member.error = finish.error
      member.displayName = finish.displayName
      touched.add(click)
      if (click.kind === 'fan-out' && outcome === 'failed') {
        notices.push({
          kind: 'error',
          message: `${bankName(finish.displayName)}: ${toastReason(finish.error || SYNC_FAILED_FALLBACK)}`,
        })
      }
    }
  }

  // Dead-half check for single clicks that just moved.
  for (const click of touched) {
    if (click.kind !== 'single') continue
    for (const member of click.members) {
      if (member.outcome !== null) continue
      const row = rowFor(member.connectionId)
      if (!row) {
        member.outcome = 'dead'
        continue
      }
      const { status, statusAt } = readLedgerSyncPipeline(row, member.pipeline)
      if (isLedgerSyncSyncStalled(status, statusAt, now)) member.outcome = 'dead'
    }
  }

  // Report and drop every click that is now fully settled.
  const remaining: SyncClick[] = []
  for (const click of clicks) {
    if (click.members.some((m) => m.outcome === null)) {
      remaining.push(click)
      continue
    }
    if (click.kind === 'single') {
      const notice = singleClickNotice(click)
      if (notice) notices.push(notice)
    }
  }
  writeClicks(queryClient, organizationId, remaining)
  return notices
}

/**
 * The name every notice leads with, so an error read off the connection list
 * says WHICH connection failed.
 *
 * @param displayName - The connection's display name, if it has one
 */
const bankName = (displayName: string | null | undefined) => displayName || 'Unknown institution'

/** The one notice of a fully settled `single` click, if any. */
function singleClickNotice(click: SyncClick): SyncClickNotice | null {
  const name = bankName(click.members.find((m) => m.displayName)?.displayName)
  const failed = click.members.filter((m) => m.outcome === 'failed')
  if (failed.length > 0) {
    const reasons = [...new Set(failed.map((m) => m.error || SYNC_FAILED_FALLBACK))]
    return { kind: 'error', message: `${name}: ${toastReason(reasons.join(' · '))}` }
  }
  const completed = click.members.filter((m) => m.outcome === 'completed')
  if (completed.length === click.members.length) {
    return { kind: 'success', message: `${name} synced` }
  }
  if (completed.length > 0) {
    const unfinished = click.members
      .filter((m) => m.outcome === 'dead')
      .map((m) => pipelineLabel(m.pipeline))
    return {
      kind: 'warning',
      message: `${name}: ${unfinished.join(' and ')} sync did not finish`,
    }
  }
  return null
}
